import { AxiosResponse } from "axios";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { CreateSharingRequest, UpdateSharingRequest } from "@/types/requests";
import { Share, SharePermission, ShareResourceType } from "@/types/resources";
import {
  CreateSharingResponse,
  GetSharesResponse,
  SearchUsersResponse,
  UpdateSharingResponse,
} from "@/types/responses";

const useShares = (resourceId: string, resourceType: ShareResourceType) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [shares, setShares] = useState<Share[]>([]);

  const client = useAxiosClient("/sharing");

  const getShares = useCallback(() => {
    if (!resourceId) return;

    client
      .get<GetSharesResponse>("", {
        params: { resource_id: resourceId, resource_type: resourceType },
      })
      .then((response) => {
        setShares(response.data.shares);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client, resourceId, resourceType]);

  // Re-enter the loading state during render (not in an effect) when the
  // resource changes, mirroring the original effect (which only reset once
  // past its `!resourceId` early return).
  const inputsKey = JSON.stringify([resourceId, resourceType]);
  const [prevInputsKey, setPrevInputsKey] = useState(inputsKey);
  if (prevInputsKey !== inputsKey) {
    setPrevInputsKey(inputsKey);
    if (resourceId) {
      setLoading(true);
      setError(null);
    }
  }

  const refresh = useCallback(() => {
    if (resourceId) {
      setLoading(true);
      setError(null);
    }
    getShares();
  }, [getShares, resourceId]);

  useEffect(() => {
    getShares();
  }, [getShares]);

  return { error, loading, refresh, setShares, shares };
};

const useCreateSharing = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/sharing");

  const createSharing = useCallback(
    async (data: CreateSharingRequest) => {
      setError(null);
      setLoading(true);
      try {
        const response = await client.post<
          CreateSharingResponse,
          AxiosResponse<CreateSharingResponse>,
          CreateSharingRequest
        >("", data);
        return response.data.share;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { createSharing, error, loading };
};

const useUpdateSharing = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/sharing");

  const updateSharing = useCallback(
    async (shareId: string, permission: SharePermission) => {
      setError(null);
      setLoading(true);
      try {
        const data: UpdateSharingRequest = { permission };
        const response = await client.patch<
          UpdateSharingResponse,
          AxiosResponse<UpdateSharingResponse>,
          UpdateSharingRequest
        >(`/${shareId}`, data);
        return response.data.share;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { error, loading, updateSharing };
};

const useDeleteSharing = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/sharing");

  const deleteSharing = useCallback(
    async (shareId: string) => {
      setError(null);
      setLoading(true);
      try {
        await client.delete(`/${shareId}`);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { deleteSharing, error, loading };
};

const useSearchUsers = () => {
  const [results, setResults] = useState<SearchUsersResponse["users"]>([]);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/sharing");

  const debouncedSearch = useMemo(
    () =>
      _.debounce(async (email: string) => {
        try {
          const response = await client.get<SearchUsersResponse>("/search", {
            params: { email },
          });
          setResults(response.data.users);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    [client]
  );

  const search = useCallback(
    (email: string) => {
      if (email.length < 3) {
        debouncedSearch.cancel();
        setResults([]);
        return;
      }

      setLoading(true);
      debouncedSearch(email);
    },
    [debouncedSearch]
  );

  const clear = useCallback(() => {
    debouncedSearch.cancel();
    setResults([]);
  }, [debouncedSearch]);

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  return { clear, loading, results, search };
};

export {
  useCreateSharing,
  useDeleteSharing,
  useSearchUsers,
  useShares,
  useUpdateSharing,
};
